import { selectModeName } from "@/store/mode-selectors";
import { useAppSelector } from "@/store/store-hooks";
import { GUID } from "@faro-lotv/foundation";
import { IElementImg360, IElementTypeHint } from "@faro-lotv/ielement-types";
import { useThree } from "@react-three/fiber";
import { useCallback, useEffect, useRef } from "react";
import { Camera, Object3D, Vector3, Vector3Tuple } from "three";
import { PointAnnotationData } from "../annotation-mutation-utils";
import {
  AnnotationShape,
  MIN_POLYGON_POINTS,
  ShapeContext,
  ShapeDraft,
  useAnnotationShapesStore,
  useIsAnnotationShapesEnabled,
} from "./annotation-shapes-store";

/** Clicking this close to the first corner, or again on the last one (a double click), closes the polygon */
const CLOSE_DISTANCE_PX = 12;

/** Corners of the unit rectangle moved and scaled by the drag controls, in its local coordinates */
const UNIT_RECTANGLE = [
  [-0.5, -0.5],
  [0.5, -0.5],
  [0.5, 0.5],
  [-0.5, 0.5],
];

/** Below this, the camera looks too flat at the sheet to project the rectangle on it */
const MIN_VIEW_DIRECTION_Y = 1e-3;

type UseShapeDrawingProps = {
  isActive: boolean;

  /** The 360 currently shown, if any */
  panoElement?: IElementImg360;

  /** Called once the shape is closed, to open the annotation form */
  onShapeClosed(shape: PointAnnotationData, kind: AnnotationShape): void;
};

type UseShapeDrawing = {
  /** True when the tool draws a rectangle or a polygon instead of a point */
  isDrawingShapes: boolean;

  shape: AnnotationShape;

  /** Handles a click on the 2D overview. @returns true if the click was used by the shape */
  onSheetClicked(point: Vector3, parentId: GUID): boolean;

  /** Handles a click on the image of the 360 */
  onPanoClicked(point: Vector3): void;

  /** Turns the rectangle moved by the drag controls into the 4 corners of the shape, on the sheet or on the 360 */
  onRectangleDragged(rectangle: Object3D, isFinished: boolean): void;

  /** Tracks the hovered point, for the polygon rubber band and to know the height of the sheet */
  onHovered(point: Vector3, sheetId?: GUID): void;
};

/** @returns the logic to draw rectangle and polygon annotations on the 2D overview and on 360s */
export function useShapeDrawing({
  isActive,
  panoElement,
  onShapeClosed,
}: UseShapeDrawingProps): UseShapeDrawing {
  const isEnabled = useIsAnnotationShapesEnabled();
  const modeName = useAppSelector(selectModeName);
  const {
    shape,
    context,
    draft,
    setContext,
    addDraftPoint,
    undoDraftPoint,
    closeDraft,
    clearDraft,
    setHoverPoint,
    setCanClosePolygon,
  } = useAnnotationShapesStore();

  const nextContext = isActive && isEnabled ? shapeContext() : undefined;

  function shapeContext(): ShapeContext | undefined {
    if (panoElement) return "pano";
    if (modeName === "sheet") return "sheet";
  }

  useEffect(() => {
    setContext(nextContext);
    if (!nextContext) clearDraft();
  }, [clearDraft, nextContext, setContext]);

  const isDrawingShapes = !!context && shape !== AnnotationShape.point;

  const camera = useThree((s) => s.camera);
  const size = useThree((s) => s.size);
  const isNearCorner = useCallback(
    (point: Vector3, corner: Vector3Tuple) =>
      toScreen(new Vector3(...corner), camera, size).distanceTo(
        toScreen(point, camera, size),
      ) < CLOSE_DISTANCE_PX,
    [camera, size],
  );

  const openDraft = draft && !draft.isClosed ? draft : undefined;
  const canClose = useCallback(
    (point: Vector3) =>
      !!openDraft &&
      openDraft.points.length >= MIN_POLYGON_POINTS &&
      isNearCorner(point, openDraft.points[0]),
    [isNearCorner, openDraft],
  );

  const addCorner = useCallback(
    (point: Vector3, target: Pick<ShapeDraft, "parentId" | "type">) => {
      const last = openDraft?.points.at(-1);
      const isDoubleClick = !!last && isNearCorner(point, last);
      if (canClose(point) || isDoubleClick) {
        closeDraft();
        return;
      }
      addDraftPoint(point.toArray(), target);
    },
    [addDraftPoint, canClose, closeDraft, isNearCorner, openDraft],
  );

  const onSheetClicked = useCallback(
    (point: Vector3, parentId: GUID) => {
      if (!isDrawingShapes || context !== "sheet") return false;
      // Rectangles are dragged, so a click only matters for polygons
      if (shape === AnnotationShape.polygon) {
        addCorner(point, { parentId, type: IElementTypeHint.mapAnnotation });
      }
      return true;
    },
    [addCorner, context, isDrawingShapes, shape],
  );

  const onPanoClicked = useCallback(
    (point: Vector3) => {
      if (!panoElement) return;
      addCorner(point.clone(), {
        parentId: panoElement.id,
        type: IElementTypeHint.spaceAnnotation,
      });
    },
    [addCorner, panoElement],
  );

  // The drag controls draw on a plane in front of the camera, so the sheet height comes from the last hover
  const lastSheetHit = useRef<{ point: Vector3; sheetId: GUID }>(undefined);

  const onRectangleDragged = useCallback(
    (rectangle: Object3D, isFinished: boolean) => {
      rectangle.updateMatrixWorld();
      const corners = UNIT_RECTANGLE.map(([x, y]) =>
        new Vector3(x, y, 0).applyMatrix4(rectangle.matrixWorld),
      );

      let target: Pick<ShapeDraft, "parentId" | "type"> | undefined;
      if (context === "pano" && panoElement) {
        target = {
          parentId: panoElement.id,
          type: IElementTypeHint.spaceAnnotation,
        };
      } else if (context === "sheet" && lastSheetHit.current) {
        const direction = camera.getWorldDirection(new Vector3());
        if (Math.abs(direction.y) < MIN_VIEW_DIRECTION_Y) return;
        const height = lastSheetHit.current.point.y;
        for (const corner of corners) {
          corner.addScaledVector(direction, (height - corner.y) / direction.y);
        }
        target = {
          parentId: lastSheetHit.current.sheetId,
          type: IElementTypeHint.mapAnnotation,
        };
      }
      if (!target) return;

      useAnnotationShapesStore.setState({
        draft: {
          ...target,
          shape: AnnotationShape.rectangle,
          points: corners.map((c): Vector3Tuple => c.toArray()),
          isClosed: isFinished,
        },
      });
    },
    [camera, context, panoElement],
  );

  const onHovered = useCallback(
    (point: Vector3, sheetId?: GUID) => {
      if (sheetId) lastSheetHit.current = { point: point.clone(), sheetId };
      if (!openDraft) return;
      setHoverPoint(point.toArray());
      setCanClosePolygon(canClose(point));
    },
    [canClose, openDraft, setCanClosePolygon, setHoverPoint],
  );

  // Keyboard shortcuts to finish, undo a corner of, or cancel the polygon being drawn
  useEffect(() => {
    if (!isActive || !draft || draft.isClosed) return;
    function onKeyDown(ev: KeyboardEvent): void {
      switch (ev.key) {
        case "Enter":
          closeDraft();
          break;
        case "Backspace":
          undoDraftPoint();
          break;
        case "Escape":
          clearDraft();
          break;
        default:
          return;
      }
      ev.preventDefault();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [clearDraft, closeDraft, draft, isActive, undoDraftPoint]);

  useEffect(() => {
    if (!draft?.isClosed) return;
    onShapeClosed(
      { points: draft.points, parentId: draft.parentId, type: draft.type },
      draft.shape,
    );
  }, [draft, onShapeClosed]);

  return {
    isDrawingShapes,
    shape,
    onSheetClicked,
    onPanoClicked,
    onRectangleDragged,
    onHovered,
  };
}

/**
 * @param point world position
 * @param camera camera rendering the scene
 * @param size size of the canvas, in pixels
 * @returns the position on screen, in pixels from the center
 */
function toScreen(
  point: Vector3,
  camera: Camera,
  size: { width: number; height: number },
): Vector3 {
  const ndc = point.clone().project(camera);
  return new Vector3((ndc.x * size.width) / 2, (ndc.y * size.height) / 2, 0);
}
